<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { FormStatus } from "./types";

const props = withDefaults(
  defineProps<{
    defaultValue?: string;
    placeholder?: string;
    disabled?: boolean;
    debounce?: number;
    rounded?: boolean;
    state?: FormStatus | null;
    type?: "neutral" | "blue";
    centerText?: boolean;
  }>(),
  {
    state: null,
    rounded: false,
    debounce: 450,
    centerText: false,
  }
);

const isInputFocused = ref(false);
const inputRef = ref<HTMLInputElement | null>(null);
const modelValue = defineModel({ default: "" }); // managed by parent or internal

onMounted(() => {
  // Set initial value only if modelValue is empty and defaultValue exists
  if (!modelValue.value && props.defaultValue) {
    modelValue.value = props.defaultValue;
  }
});

const emit = defineEmits<{
  (e: "change", value: string): void;
  (e: "enter", value: string): void;
}>();

function focusInput() {
  inputRef.value?.focus();
}

const handleInput = (e: Event) => {
  const value = (e.target as HTMLInputElement).value;
  modelValue.value = value;
  emit("change", value);
};

const handleKeyPress = (e: KeyboardEvent) => {
  if (e.key === "Enter") {
    emit("enter", modelValue.value);
  }
};

const stateError = computed(() => {
  switch (props.state?.type) {
    case "error":
      return "!border-red-800";
    case "warning":
      return "border-orange-500";
    case "success":
      return "border-green-500";
    default:
      return "";
  }
});

const focused = computed(() => (isInputFocused.value ? "focused" : ""));
</script>

<template>
  <div>
    <div
      @click="focusInput"
      class="border border-transparent transition-colors px-3 py-2 flex gap-1 items-center text-neutral-200 bg-neutral-800 cursor-text"
      :class="[focused, stateError, rounded ? 'rounded-full' : 'rounded-md']"
    >
      <slot name="left-icon" />
      <input
        v-model="modelValue"
        ref="inputRef"
        type="text"
        @keypress="handleKeyPress"
        @focus="isInputFocused = true"
        @blur="isInputFocused = false"
        @input="handleInput"
        :placeholder="placeholder"
        :disabled="disabled"
        :class="centerText ? 'text-center' : ''"
      />
      <slot name="right-icon" />
    </div>
    <div v-if="props.state" class="text-red-500 text-sm mt-1">{{ props.state.msg }}</div>
  </div>
</template>

<style scoped lang="scss">
input {
  @apply border-transparent bg-transparent outline-none w-full;
  box-shadow: none;
  border-radius: 0;
  &::placeholder {
    @apply text-neutral-500;
  }
  &:focus::placeholder {
    @apply text-grey-80;
  }
}

.focused {
  @apply border-blue-700 text-white;
}
</style>
